import React, { useState } from 'react';
import { Activity, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import {
  useContractHealth,
  useContractEvents,
  useApplyContractResolution,
} from '../../hooks/useContractNegotiation.js';
import type { ContractEdgeHealth, ContractEvent } from '../../generated/api/types.gen.js';
import { Can } from '../auth/Can.js';
import { Button, Input, Spinner } from '../ui/index.js';

interface ContractHealthPanelProps {
  workflowId?: number;
  runId?: number;
}

export const ContractHealthPanel: React.FC<ContractHealthPanelProps> = ({ workflowId, runId }) => {
  const { data: healthData, isLoading: healthLoading } = useContractHealth(workflowId);
  const { data: eventsData } = useContractEvents(runId);

  const health = healthData?.health;
  const events = eventsData?.events ?? [];
  const edges = health?.edges ?? [];

  if (healthLoading) {
    return (
      <div className="p-4 text-muted text-sm flex items-center gap-2">
        <Spinner />
        Loading contract health...
      </div>
    );
  }

  if (!health) {
    return (
      <div className="p-4 text-faint text-sm">
        No contract data available. Define contracts on workflow steps to enable health monitoring.
      </div>
    );
  }

  const ratio = health.healthRatio;
  const ratioColor = ratio >= 0.8 ? 'text-success' : ratio >= 0.5 ? 'text-warning' : 'text-danger';
  const ratioIcon = ratio >= 0.8 ? CheckCircle : ratio >= 0.5 ? AlertTriangle : XCircle;
  const RatioIcon = ratioIcon;

  return (
    <div className="space-y-4">
      {/* Health Summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <HealthStat
          label="Health"
          value={`${Math.round(ratio * 100)}%`}
          icon={<RatioIcon size={14} className={ratioColor} />}
        />
        <HealthStat
          label="Compatible"
          value={health.compatible}
          icon={<CheckCircle size={14} className="text-success" />}
        />
        <HealthStat
          label="Mismatches"
          value={health.mismatches}
          icon={<AlertTriangle size={14} className="text-warning" />}
        />
        <HealthStat label="Disputes" value={health.disputes} icon={<XCircle size={14} className="text-danger" />} />
      </div>

      {/* Recent Violations */}
      {Object.keys(health.recentViolations).length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-heading mb-2 uppercase tracking-wide">Recent Violations</h4>
          <div className="space-y-1">
            {Object.entries(health.recentViolations).map(([type, count]) => (
              <div
                key={type}
                className="flex items-center justify-between px-3 py-1.5 bg-surface-alt rounded-sm text-sm"
              >
                <span className="text-body">{formatEventType(type)}</span>
                <span className="text-muted font-mono">{count as number}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {edges.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-heading mb-2 uppercase tracking-wide">Contract Edges</h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {edges.slice(0, 12).map((edge: ContractEdgeHealth) => (
              <div
                key={`${edge.sourceStepId}:${edge.targetStepId}`}
                className="px-3 py-1.5 bg-surface rounded-sm text-xs"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="text-body">{edge.sourceStepName}</span>
                    <span className="text-faint"> to </span>
                    <span className="text-body">{edge.targetStepName}</span>
                    {edge.missingFields.length > 0 && (
                      <div className="text-faint truncate">Missing: {edge.missingFields.join(', ')}</div>
                    )}
                  </div>
                  <span className={edgeStatusClass(edge.status)}>{formatEventType(edge.status)}</span>
                </div>
                {edge.status === 'mismatch' && (edge.suggestions?.length ?? 0) > 0 && workflowId && (
                  <Can policy="CanManageWorkflows">
                    <EdgeApplyFix edge={edge} workflowId={workflowId} />
                  </Can>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Events */}
      {events.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-heading mb-2 uppercase tracking-wide">Recent Events</h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {events.slice(0, 10).map((event: ContractEvent) => (
              <div key={event.id} className="flex items-start gap-2 px-3 py-1.5 bg-surface rounded-sm text-xs">
                <SeverityDot severity={event.severity} />
                <div className="flex-1 min-w-0">
                  <span className="text-body">{formatEventType(event.eventType)}</span>
                  {(event.details as { stepName?: string } | null | undefined)?.stepName && (
                    <span className="text-faint ml-2">Step: {(event.details as { stepName?: string }).stepName}</span>
                  )}
                </div>
                <span className="text-faint whitespace-nowrap">{new Date(event.createdAt).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {health.totalEdges === 0 && events.length === 0 && (
        <div className="text-center py-6 text-faint text-sm">
          <Activity size={20} className="mx-auto mb-2 opacity-50" />
          No contract activity yet
        </div>
      )}
    </div>
  );
};

// ── EdgeApplyFix ──

interface EdgeApplyFixProps {
  edge: ContractEdgeHealth;
  workflowId: number;
  /** For testing: start expanded */
  defaultOpen?: boolean;
}

export function EdgeApplyFix({ edge, workflowId, defaultOpen = false }: EdgeApplyFixProps) {
  const mappings = edge.suggestions?.[0]?.mappings ?? [];
  const [open, setOpen] = useState(defaultOpen);
  const [sources, setSources] = useState<Record<string, string>>(() =>
    Object.fromEntries(mappings.map((m) => [m.target, m.source])),
  );
  const mutation = useApplyContractResolution(workflowId);

  if (!mappings.length) return null;

  const handleApply = () => {
    mutation.mutate(
      {
        sourceStepId: Number(edge.sourceStepId),
        mappings: Object.entries(sources).map(([target, source]) => ({ source, target })),
      },
      { onSuccess: () => setOpen(false) },
    );
  };

  if (!open) {
    return (
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Apply fix
      </Button>
    );
  }

  return (
    <div className="mt-2 space-y-1.5 rounded-sm border bg-surface-alt p-2">
      {mappings.map((m) => (
        <div key={m.target} className="flex items-center gap-2 text-xs">
          <span className="shrink-0 font-mono text-body">{m.target}</span>
          <span className="text-faint">←</span>
          <Input
            value={sources[m.target] ?? ''}
            onChange={(e) => setSources((prev) => ({ ...prev, [m.target]: e.target.value }))}
            list={edge.missingFields.length > 0 ? `fix-hints-${edge.sourceStepId}-${m.target}` : undefined}
            placeholder="source field"
            aria-label={`Source field for ${m.target}`}
            className="min-w-0 flex-1"
          />
          {edge.missingFields.length > 0 && (
            <datalist id={`fix-hints-${edge.sourceStepId}-${m.target}`}>
              {edge.missingFields.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
          )}
        </div>
      ))}
      {mutation.error && (
        <p className="text-[10px] text-danger">
          {mutation.error instanceof Error ? mutation.error.message : 'Apply failed'}
        </p>
      )}
      <div className="flex items-center gap-2 pt-0.5">
        <Button size="sm" onClick={handleApply} loading={mutation.isPending}>
          {mutation.isPending ? 'Applying…' : 'Apply'}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Helpers ──

function HealthStat({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) {
  return (
    <div className="bg-surface rounded-sm p-3 border">
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <span className="text-xs text-muted">{label}</span>
      </div>
      <span className="text-lg font-semibold text-heading">{value}</span>
    </div>
  );
}

function SeverityDot({ severity }: { severity: string }) {
  const colors: Record<string, string> = {
    error: 'bg-danger',
    warning: 'bg-warning-muted',
    info: 'bg-accent',
  };
  return <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1 ${colors[severity] ?? 'bg-surface-alt'}`} />;
}

function formatEventType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function edgeStatusClass(status: string): string {
  const colors: Record<string, string> = {
    compatible: 'text-success',
    mismatch: 'text-warning',
    resolved: 'text-success',
    dispute: 'text-danger',
    unknown: 'text-faint',
  };
  return `text-[10px] font-medium uppercase ${colors[status] ?? 'text-faint'}`;
}
